import { Injectable, EventEmitter, inject, signal, effect } from '@angular/core';
import { Observable, Subject, from } from 'rxjs';
import { PouchDB } from '../db/pouchdb-init';
import { BasePouchDbService } from '../db/base-pouchdb.service';
import { BaseProjectDocument } from '../types';
import { ProjectProvider } from '../interfaces/project-provider.interface';
import { PROJECT_ROLES_CONFIG, STRUCTURE_PROVIDERS } from '../tokens';
import { Mutex } from '../utils/mutex';

/**
 * Generische Basis-Implementierung für Projekt-Services mit PouchDB.
 * Bietet universelle Projekt-Verwaltung, CRUD, Checkout/Checkin,
 * Key-Value-Storage, Attachment-Management und Offline-Support.
 *
 * Diese Klasse ist komplett generisch und enthält keine App-spezifische Logik.
 * Subklassen müssen implementieren:
 * - refreshProjectList(): Projektliste mit Auth-Filterung
 * - canI(access, resource): Permission-Primitive
 * - amI(role): Rollen-Primitive
 *
 * Optional überschreibbar (haben sinnvolle Defaults):
 * - generateProjectId(), canIbe(), isOwner(), amIMember(),
 *   canEditRegions(), canCreateRegions(), canDeleteRegions(), canAssignToProject()
 */
@Injectable()
export abstract class BaseProjectService<T extends BaseProjectDocument>
	extends BasePouchDbService implements ProjectProvider<T> {

	// ========== Abstract Service Getters (Pattern von BaseDocumentService) ==========

	protected abstract get storage(): any; // StorageService
	protected abstract get oms(): any; // OfflineModeService
	protected abstract get auth(): any; // AuthProvider

	/** Connection-Monitoring Observable. App liefert z.B. ConnectionService.monitor(). */
	protected abstract get connectionMonitor(): Observable<boolean>;

	/** Auth-Changed Observable. App liefert z.B. AuthService.authChanged. */
	protected abstract get authChanged(): Observable<void>;

	/** Optional: Toast-Service. Null wenn nicht verfügbar. */
	protected get msg(): any | null { return null; }

	// ========== Abstract Methods (app-spezifisch) ==========

	/**
	 * Aktualisiert die Projektliste basierend auf dem aktuellen Modus.
	 * App-spezifisch: Kann nach Manager-Rolle filtern, Views nutzen, etc.
	 */
	protected abstract refreshProjectList(): Promise<void>;

	/**
	 * Generiert eine Projekt-ID aus dem Projektnamen.
	 * Default: Lowercase mit Bindestrichen.
	 * App kann überschreiben für Datums-basierte, GUID-basierte, etc. IDs.
	 */
	protected generateProjectId(name: string): string {
		return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
	}

	// Permission-Methoden: canI und amI sind abstrakt (fundamentale Primitiven),
	// die übrigen haben sinnvolle Defaults basierend auf canI/amI.
	abstract canI(access: string, resource: string): boolean;
	abstract amI(role: string): boolean;

	/** Default: Delegiert an amI(). */
	canIbe(role: string): boolean { return this.amI(role); }
	/** Default: false (kein Owner-Konzept ohne explizite Implementierung). */
	isOwner(): boolean { return false; }
	/** Default: true (jeder ist Mitglied). */
	amIMember(): boolean { return true; }
	/** Default: Delegiert an canI('write', 'region'). */
	canEditRegions(): boolean { return this.canI('write', 'region'); }
	/** Default: Delegiert an canI('add', 'region'). */
	canCreateRegions(): boolean { return this.canI('add', 'region'); }
	/** Default: Delegiert an canI('delete', 'region'). */
	canDeleteRegions(): boolean { return this.canI('delete', 'region'); }
	/** Default: Delegiert an canI('write', 'project'). */
	canAssignToProject(): boolean { return this.canI('write', 'project'); }

	// ========== Injected Config ==========
	private rolesConfig = inject(PROJECT_ROLES_CONFIG);
	private structureProviders: any[] = inject(STRUCTURE_PROVIDERS, { optional: true }) ?? [];

	// ========== Hooks mit Default-Implementierung ==========

	/**
	 * Hook: CouchDB Design-Dokumente initialisieren.
	 * Default: keine Aktion. App kann überschreiben.
	 */
	protected async initDesignDocs(db: PouchDB.Database): Promise<void> { }

	/**
	 * Hook: Wird nach dem Speichern des aktuellen Projekts aufgerufen.
	 * Default: keine Aktion. App kann z.B. authChanged emittieren.
	 */
	protected onProjectSaved(doc: any): void { }

	// ========== Public Properties & Signals ==========

	private dbSynched = false;
	public isBusy = false;
	public override isInitialized = false;

	connected = new Subject<boolean>();

	checkedOut = new EventEmitter<void>(true);
	checkedIn = new EventEmitter<void>(true);
	discarded = new EventEmitter<void>(true);
	initialized = new EventEmitter<void>(true);
	projectSaved = new EventEmitter<any>(true);

	currentDb_ = new Subject<PouchDB.Database>();

	projectList = signal<T[]>([]);
	project = signal<string>('');

	// True wenn das aktuelle Projekt aus dem Storage wiederhergestellt wird (Page-Reload)
	isRestoringProject = signal<boolean>(false);

	// Gecachtes Projektdokument für Berechtigungsprüfungen
	currentProjectDoc = signal<T | null>(null);

	// True wenn es ungesynchronisierte lokale Änderungen gibt
	hasPendingChanges = signal<boolean>(false);

	// Mutex für serialisierten Zugriff auf keyvalue-Speicher
	private _keyvalueMutex = new Mutex();

	// Debounce für refreshProjectList
	private _refreshProjectListTimer?: ReturnType<typeof setTimeout>;

	// ========== BasePouchDbService Default-Implementierungen ==========

	get localDbName(): string {
		return 'projects';
	}

	protected get projectSignal() {
		// ProjectService hat kein projekt-basiertes Switching wie DataService
		return signal<string | null>(null);
	}

	protected async onInitializeRemoteDatabase(db: PouchDB.Database): Promise<void> {
		await this.ensureApplicationDesignDocument(db);
		await this.initDesignDocs(db);
		await this.refreshProjectList();
	}

	protected async onInitializeLocalDatabase(db: PouchDB.Database): Promise<void> {
		await this.ensureApplicationDesignDocument(db);
	}

	protected onDatabasesInitialized(): void {
		this.isInitialized = true;
		this.initialized.emit();
		if (this.db()) {
			this.currentDb_.next(this.db()!);
			this.refreshProjectList();
		}
	}

	protected onDatabasesClosed(): void {
		// Default: keine spezielle Logik
	}

	protected override onLiveSyncChange(info: any): void {
		if (info.direction === 'pull') {
			this.refreshProjectList();
		}
	}

	protected async shouldKeepEmptyLocalDb(dbName: string): Promise<boolean> {
		return this.oms.userOfflineMode();
	}

	async ensureApplicationDesignDocument(db: PouchDB.Database) { }

	// ========== Initialization ==========

	/**
	 * Muss vom Subklassen-Konstruktor aufgerufen werden,
	 * nachdem alle Dependencies verfügbar sind.
	 */
	protected initializeBaseProjectService(): void {
		// Connection-Monitoring
		this.connectionMonitor.subscribe((online: boolean) => {
			this.connected.next(online);
			if (online && !this.oms.userOfflineMode()) {
				this._initRemoteDatabase(() => {});
			}
		});

		this._initDatabases();

		// Aktuelles Projekt nach DB-Initialisierung wiederherstellen
		this.initialized.subscribe(() => {
			this.storage.get('currentProject').then(async (projectName: string | null) => {
				if (projectName) {
					this.isRestoringProject.set(true);
					await this.loadProject(projectName);
					setTimeout(() => this.isRestoringProject.set(false), 500);
				} else {
					this.unloadCurrentProject();
				}
			});
		});

		// Bei Offline-Modus-Wechsel Projektliste aktualisieren
		let previousUserOfflineMode = this.oms.userOfflineMode();
		effect(() => {
			const userOffline = this.oms.userOfflineMode();
			if (userOffline !== previousUserOfflineMode) {
				previousUserOfflineMode = userOffline;
				if (this.isInitialized) {
					this.refreshProjectList();
				}
			}
		});

		// Bei Auth-Änderungen Projektliste aktualisieren (debounced)
		this.authChanged.subscribe(() => {
			if (this.isInitialized && this.remoteDb) {
				if (this._refreshProjectListTimer) clearTimeout(this._refreshProjectListTimer);
				this._refreshProjectListTimer = setTimeout(() => this.refreshProjectList(), 300);
			}
		});
	}

	// ========== Project List Query ==========

	__projectList(online: boolean): Observable<PouchDB.Core.AllDocsResponse<any>> {
		if (online && this.remoteDb) {
			return from(this.remoteDb.allDocs({ include_docs: false }));
		} else if (!online && this.localDb) {
			return from(this.localDb.allDocs({ include_docs: false }));
		} else {
			return from(Promise.resolve({ rows: [], total_rows: 0, offset: 0 } as PouchDB.Core.AllDocsResponse<any>));
		}
	}

	// ========== Checkout / Checkin ==========

	public checkout(project: string): void {
		if (!this.remoteDb) return;
		this.isBusy = true;
		this.dbSynched = false;
		const mayBeReady = () => {
			if (this.dbSynched) {
				this.localDb!.on('error', (err: any) => {
					debugger;
				});
				// Structure-Daten herunterladen bevor checkedOut emittiert wird
				this.downloadStructuresForProject(project).then(() => {
					this.checkedOut.emit();
					this.dbSynched = false;
					this.isBusy = false;
					this.currentDb_.next(this.localDb!);
				}).catch((err) => {
					console.error('Fehler beim Structure-Download:', err);
					// Checkout trotzdem abschließen auch wenn Structures fehlschlagen
					this.checkedOut.emit();
					this.dbSynched = false;
					this.isBusy = false;
					this.currentDb_.next(this.localDb!);
				});
			}
		};
		const syncDb = () => {
			try {
				this.localDb = new PouchDB(project);
			} catch (err) {
				this.isBusy = false;
				return;
			}
			const replOptions: any = {
				batch_size: 10,
				live: false,
				retry: true,
				style: 'main_only'
			};
			replOptions.doc_ids = [project];
			this.localDb.replicate.from(this.remoteDb!, replOptions)
			.on('change', (result: any) => {})
			.on('complete', () => {
				this.dbSynched = true;
				mayBeReady();
			})
			.catch(() => {
				this.isBusy = false;
			});
		};

		if (this.localDb)
			this.localDb.destroy().then(syncDb);
		else
			syncDb();
	}

	async checkinDb() {
		if (!this.isDbCheckedOut())
			return;

		this.isBusy = true;
		if (!this.remoteDb) return;

		// Structure-Services zuerst synchronisieren und verwerfen
		await this.syncStructures();

		const sync = () => {
			PouchDB.replicate(this.localDb!, this.remoteDb!, { batch_size: 10 }
			).on('change', result => {}
			).on('complete', async () => {
				await this.localDb?.destroy();
				this.localDb = undefined;
				if (this.remoteDb) this.currentDb_.next(this.remoteDb);
				this.isBusy = false;
				this.checkedIn.emit();
			}).catch((err: any) => {
				this.isBusy = false;
				console.error(err);
			});
		};

		sync();
	}

	override async discardLocal() {
		this.isBusy = true;
		// Structure-Services zuerst verwerfen
		await this.discardStructures();
		await this.localDb?.destroy();
		delete this.localDb;
		this.storage.clearProject(this.project(), true);
		this.isBusy = false;
		this.discarded.emit();
	}

	/**
	 * Synchronisiert lokale Projektänderungen zur Remote-Datenbank.
	 * Bei Konflikten gewinnt die lokale Version.
	 */
	async syncProjectToRemote(): Promise<boolean> {
		if (!this.localDb) return false;

		// Remote-DB on-demand aufbauen (wichtig wenn aus Offline-Modus gesynced wird)
		if (!this.remoteDb) {
			const connected = await this.ensureRemoteDb();
			if (!connected) return false;
		}
		const remoteDb = this.remoteDb!;

		try {
			// Pending IndexedDB-Werte ins lokale PouchDB-Dokument übertragen,
			// damit sie bei der Replikation zum Server gelangen.
			// Im Offline-Modus speichert setProjectValue() nur in IndexedDB.
			const projectId = this.project();
			if (projectId) {
				const all = await this.storage.getProject(projectId);
				if (all && all.length > 0) {
					await (this.localDb as any).upsert(projectId, (doc: any) => {
						all.forEach((p: { key: string; value: any }) => {
							doc[p.key] = p.value;
						});
						return doc;
					});
				}
			}

			const SYNC_TIMEOUT_MS = 30000;
			await new Promise<void>((resolve, reject) => {
				const timeoutId = setTimeout(() => {
					reject(new Error('Synchronisation Timeout nach 30 Sekunden'));
				}, SYNC_TIMEOUT_MS);

				PouchDB.replicate(this.localDb!, remoteDb, { batch_size: 10 })
					.on('complete', () => {
						clearTimeout(timeoutId);
						resolve();
					})
					.on('error', (err) => {
						clearTimeout(timeoutId);
						reject(err);
					});
			});

			// Konflikte auflösen (lokale Version gewinnt)
			const allDocs = await remoteDb.allDocs({ conflicts: true, include_docs: true });
			for (const row of allDocs.rows) {
				const doc = row.doc as any;
				if (doc?._conflicts && doc._conflicts.length > 0) {
					for (const conflictRev of doc._conflicts) {
						await remoteDb.remove(doc._id, conflictRev);
					}
				}
			}

			this.hasPendingChanges.set(false);
			return true;
		} catch (error) {
			console.error('Fehler beim Synchronisieren des Projekts:', error);
			return false;
		}
	}

	isDbCheckedOut(): boolean {
		return !this.isBusy && !!this.localDb;
	}

	// ========== Structure Services Integration ==========

	/**
	 * Lädt Structure-Daten für ein Projekt herunter.
	 * Wird automatisch nach dem Project-Checkout aufgerufen.
	 */
	private async downloadStructuresForProject(projectId: string): Promise<void> {
		if (!this.structureProviders.length) return;
		if (!this.localDb) return;

		try {
			const projectDoc = await this.localDb.get(projectId);
			for (const service of this.structureProviders) {
				await service.downloadForProject(projectDoc);
			}
		} catch (err) {
			console.error('Fehler beim Laden des Projekt-Dokuments für Structure-Download:', err);
		}
	}

	/**
	 * Synchronisiert alle Structure-Services zurück zur Remote-DB und verwirft lokale DBs.
	 * Wird beim Checkin aufgerufen.
	 */
	private async syncStructures(): Promise<void> {
		for (const service of this.structureProviders) {
			try {
				await service.syncAndDiscard();
			} catch (err) {
				console.error('Fehler beim Sync eines Structure Service:', err);
			}
		}
	}

	/**
	 * Verwirft alle lokalen Structure-DBs ohne Synchronisation.
	 * Wird beim Discard aufgerufen.
	 */
	private async discardStructures(): Promise<void> {
		for (const service of this.structureProviders) {
			try {
				await service.discardLocal();
			} catch (err) {
				console.error('Fehler beim Verwerfen eines Structure Service:', err);
			}
		}
	}

	// ========== Offline Downloads ==========

	/**
	 * Lädt mehrere Projekt-Dokumente für die Offline-Nutzung herunter.
	 */
	async downloadProjectsForOffline(projectIds: string[]): Promise<void> {
		if (!this.remoteDb || projectIds.length === 0) return;

		if (!this.localDb) {
			this.localDb = new PouchDB('projects');
		}

		await this.localDb.replicate.from(this.remoteDb, {
			doc_ids: projectIds
		});
	}

	/**
	 * Lädt ein Projekt-Dokument für die Offline-Nutzung herunter.
	 */
	async downloadProjectForOffline(projectId: string): Promise<void> {
		await this.downloadProjectsForOffline([projectId]);
	}

	/**
	 * Prüft ob ein Projekt offline verfügbar ist.
	 */
	async isProjectAvailableOffline(projectId: string): Promise<boolean> {
		if (!this.localDb) return false;

		try {
			await this.localDb.get(projectId);
			return true;
		} catch {
			return false;
		}
	}

	/**
	 * Aktualisiert die lokalen Projekt-Daten vom Server.
	 */
	async refreshProjectDataFromRemote(projectId?: string): Promise<void> {
		const id = projectId ?? this.project();
		if (!id || !this.remoteDb || this.oms.userOfflineMode()) return;

		try {
			const doc = await this.remoteDb.get<any>(id);

			const baseExcludeKeys = ['_id', '_rev', 'name', 'description', 'owner', 'type'];
			const roleFields = this.rolesConfig.memberRoles.map(r => r.field);
			const excludeKeys = [...baseExcludeKeys, ...roleFields];

			for (const [key, value] of Object.entries(doc)) {
				if (!excludeKeys.includes(key) && !key.startsWith('_')) {
					await this.storage.setProjectValue(key, id, value, 'keyvalue');
				}
			}
		} catch (e) {
			console.error('Fehler beim Aktualisieren der Projekt-Daten vom Server:', e);
		}
	}

	// ========== CRUD ==========

	getAll(include_docs = false) {
		return this.db()?.allDocs({ include_docs: include_docs });
	}

	async getOne(id: string) {
		return await this.db()?.get(id);
	}

	async save(doc: any): Promise<void> {
		const updatedDoc = await this.db()?.get(doc._id);
		if (!updatedDoc) return;
		doc._rev = updatedDoc._rev;
		try {
			const result = await this.db()?.put(doc);
			if (result) {
				this.projectList.update(list => list.map(p =>
					p._id === doc._id ? { ...p, name: doc.name, description: doc.description } : p
				));
				if (doc._id === this.project()) {
					this.currentProjectDoc.set({ ...doc, _rev: result.rev } as T);
					this.onProjectSaved(doc);
				}
				this.projectSaved.emit(doc);
			}
		} catch (error) {
			console.error(error);
		}
	}

	async create(project: any): Promise<T | undefined> {
		const id = this.generateProjectId(project.name);
		const doc = { ...project, _id: id } as T;
		try {
			await this.db()?.put(doc);
			this.projectList.update(list => [...list, doc]);
			this.projectSaved.emit(doc);
			return doc;
		} catch (error) {
			console.error('Fehler beim Erstellen des Projekts:', error);
			return undefined;
		}
	}

	/**
	 * Löscht ein Projekt aus der Datenbank.
	 */
	async deleteProject(projectId: string): Promise<boolean> {
		if (!projectId) return false;

		try {
			// Aus Remote-DB löschen
			if (this.remoteDb) {
				try {
					const doc = await this.remoteDb.get(projectId, { conflicts: true });
					await this.remoteDb.remove(doc._id, doc._rev);
					if (doc._conflicts) {
						for (const rev of doc._conflicts) {
							try {
								await this.remoteDb.remove(doc._id, rev);
							} catch (e) { /* Konflikt-Revision ignorieren */ }
						}
					}
				} catch (e: any) {
					if (e.status !== 404) {
						console.error('Fehler beim Löschen aus Remote-DB:', e);
					}
				}
			}

			// Aus lokaler DB löschen
			if (this.localDb) {
				try {
					const doc = await this.localDb.get(projectId, { conflicts: true });
					await this.localDb.remove(doc._id, doc._rev);
					if (doc._conflicts) {
						for (const rev of doc._conflicts) {
							try {
								await this.localDb.remove(doc._id, rev);
							} catch (e) { /* Konflikt-Revision ignorieren */ }
						}
					}
				} catch (e: any) {
					if (e.status !== 404) {
						console.error('Fehler beim Löschen aus lokaler DB:', e);
					}
				}
			}

			// Lokalen Storage löschen
			await this.storage.clearProject(projectId);

			// Projektliste aktualisieren
			this.projectList.update(list => list.filter(p => p._id !== projectId));

			// Wenn aktives Projekt, entladen
			if (this.project() === projectId) {
				await this.unloadCurrentProject();
			}

			return true;
		} catch (error) {
			console.error('Fehler beim Löschen des Projekts:', error);
			return false;
		}
	}

	// ========== Key-Value Storage ==========

	async getProjectValue(key: string, store = 'keyvalue') {
		if (!this.project()) return;
		return await this.storage.getProjectValue(key, this.project(), store);
	}

	async setProjectValue(key: string, value: any, store = 'keyvalue', markAsPending = true) {
		if (!this.project()) return;

		if (store === 'keyvalue') {
			const unlock = await this._keyvalueMutex.lock();
			try {
				await this.storage.setProjectValue(key, this.project(), value, store);

				const database = this.db();
				if (!database) {
					if (markAsPending && this.oms.userOfflineMode()) {
						this.hasPendingChanges.set(true);
					}
					return;
				}

				if (markAsPending && this.oms.userOfflineMode()) {
					this.hasPendingChanges.set(true);
				}

				if (!this.oms.networkOnline()) {
					return;
				}

				const projectId = this.project();
				const all = await this.storage.getProject(projectId);

				await (database as any).upsert(projectId, (doc: any) => {
					all?.forEach((p: { key: string; value: any; }) => {
						doc[p.key] = p.value;
					});
					return doc;
				});
			} catch (e) {
				if (!this.oms.networkOnline()) {
					return;
				}
				if (this.msg) {
					this.msg.error("Fehler beim Speichern von Projektwerten!");
				}
			} finally {
				unlock();
			}
		} else {
			await this.storage.setProjectValue(key, this.project(), value, store);
		}
	}

	async removeProjectValue(key: string, store = 'keyvalue') {
		if (!this.project()) return;
		return await this.storage.removeProjectValue(key, this.project(), store);
	}

	async numberOfKeys(store = 'keyvalue'): Promise<number | undefined> {
		if (!this.project()) return;
		return await this.storage.numberOfKeys(this.project(), store);
	}

	async clearProject() {
		if (!this.project()) return;
		return await this.storage.clearProject(this.project());
	}

	async listKeys(store = 'keyvalue'): Promise<string[] | undefined> {
		if (!this.project()) return;
		return await this.storage.listKeys(this.project(), store);
	}

	// ========== Project Load / Unload ==========

	async unloadCurrentProject() {
		await this.storage.remove('currentProject');
		this.currentProjectDoc.set(null);
		this.project.set('');
	}

	async loadProject(projectName: string) {
		if (!this.db()) return;
		await this.storage.set('currentProject', projectName);
		const isProjectSwitch = this.project() !== projectName;
		if (isProjectSwitch) {
			this.hasPendingChanges.set(false);
		}
		try {
			const prjDoc = await this.db()?.get(projectName) as T;
			if (!prjDoc) {
				if (this.msg) {
					this.msg.error(`Projekt "${projectName}" konnte nicht geladen werden!`);
				}
				await this.unloadCurrentProject();
				return;
			}
			if (isProjectSwitch) {
				await this.storage.setProject(prjDoc);
			}
			this.currentProjectDoc.set(prjDoc);
			this.project.set(projectName);
		}
		catch (e: any) {
			if (this.msg) {
				this.msg.error(`Projekt "${projectName}" konnte nicht geladen werden!`);
			}
			await this.unloadCurrentProject();
			console.error(e);
		}
	}

	// ========== Geodata Files ==========

	/**
	 * Fügt eine Geodatendatei zum Projekt-Dokument hinzu.
	 */
	async addGeodataFile(filename: string): Promise<void> {
		const projectId = this.project();
		if (!projectId) {
			console.warn('addGeodataFile: Kein Projekt aktiv');
			return;
		}

		const database = this.db();
		if (!database) {
			console.warn('addGeodataFile: Keine Datenbank verfügbar');
			return;
		}

		try {
			await (database as any).upsert(projectId, (doc: any) => {
				if (!doc.geodataFiles) {
					doc.geodataFiles = [];
				}
				if (!doc.geodataFiles.includes(filename)) {
					doc.geodataFiles.push(filename);
				}
				return doc;
			});
		} catch (e) {
			console.error('Fehler beim Hinzufügen der Geodatendatei:', e);
		}
	}

	/**
	 * Entfernt eine Geodatendatei aus dem Projekt-Dokument.
	 */
	async removeGeodataFile(filename: string): Promise<void> {
		const projectId = this.project();
		if (!projectId) return;

		const database = this.db();
		if (!database) return;

		try {
			await (database as any).upsert(projectId, (doc: any) => {
				if (!doc.geodataFiles) return doc;
				const index = doc.geodataFiles.indexOf(filename);
				if (index > -1) {
					doc.geodataFiles.splice(index, 1);
				}
				return doc;
			});
		} catch (e) {
			console.error('Fehler beim Entfernen der Geodatendatei:', e);
		}
	}

	// ========== Attachments ==========

	/**
	 * Fügt ein Attachment zum Projektdokument hinzu.
	 */
	async addProjectAttachment(projectId: string, filename: string, blob: Blob, contentType: string): Promise<boolean> {
		const database = this.db();
		if (!database) return false;

		try {
			const doc = await database.get(projectId);
			await database.putAttachment(projectId, filename, doc._rev!, blob, contentType);
			return true;
		} catch (e) {
			console.error('Fehler beim Hinzufügen des Anhangs:', e);
			return false;
		}
	}

	/**
	 * Lädt ein Attachment aus dem Projektdokument.
	 */
	async getProjectAttachment(projectId: string, filename: string): Promise<Blob | null> {
		const database = this.db();
		if (!database) return null;

		try {
			return await database.getAttachment(projectId, filename) as Blob;
		} catch (e) {
			console.error('Fehler beim Laden des Anhangs:', e);
			return null;
		}
	}

	/**
	 * Entfernt ein Attachment aus dem Projektdokument.
	 */
	async removeProjectAttachment(projectId: string, filename: string): Promise<boolean> {
		const database = this.db();
		if (!database) return false;

		try {
			const doc = await database.get(projectId);
			await database.removeAttachment(projectId, filename, doc._rev!);
			return true;
		} catch (e) {
			console.error('Fehler beim Entfernen des Anhangs:', e);
			return false;
		}
	}

	/**
	 * Aktualisiert die Attachment-Metadaten im Projektdokument.
	 */
	async updateAttachmentsMeta(projectId: string, meta: any[]): Promise<void> {
		const database = this.db();
		if (!database) return;

		try {
			await (database as any).upsert(projectId, (doc: any) => {
				doc.attachmentsMeta = meta;
				return doc;
			});
		} catch (e) {
			console.error('Fehler beim Aktualisieren der Attachment-Metadaten:', e);
		}
	}
}
